export interface APIUsageData {
  provider: 'gemini-free' | 'gemini' | 'claude' | 'gpt';
  date: string; // YYYY-MM-DD format
  usage: {
    requests: number;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    estimatedCost?: number; // in USD
  };
}

export interface APIQuota {
  provider: 'gemini-free' | 'gemini' | 'claude' | 'gpt';
  limits: {
    requestsPerMinute?: number;
    requestsPerDay?: number;
    tokensPerMinute?: number;
    tokensPerDay?: number;
    tokensPerMonth?: number;
  };
  pricing?: {
    inputTokensPer1K: number; // USD per 1K tokens
    outputTokensPer1K: number; // USD per 1K tokens
  };
}

// API制限とコスト情報
const API_QUOTAS: Record<string, APIQuota> = {
  'gemini-free': {
    provider: 'gemini-free',
    limits: {
      requestsPerMinute: 60,
      requestsPerDay: 1500,
      tokensPerMinute: 32000,
      tokensPerMonth: 1000000,
    },
    // 無料版はコストなし
  },
  'gemini': {
    provider: 'gemini',
    limits: {
      requestsPerMinute: 360,
      tokensPerMinute: 120000,
    },
    pricing: {
      inputTokensPer1K: 0.00025, // $0.25 per 1M tokens
      outputTokensPer1K: 0.00125, // $1.25 per 1M tokens
    },
  },
  'claude': {
    provider: 'claude',
    limits: {
      requestsPerMinute: 50,
      tokensPerMinute: 100000,
    },
    pricing: {
      inputTokensPer1K: 0.008, // $8 per 1M tokens (Claude 3 Sonnet)
      outputTokensPer1K: 0.024, // $24 per 1M tokens
    },
  },
  'gpt': {
    provider: 'gpt',
    limits: {
      requestsPerMinute: 500,
      tokensPerMinute: 150000,
    },
    pricing: {
      inputTokensPer1K: 0.003, // $3 per 1M tokens (GPT-4 Turbo)
      outputTokensPer1K: 0.010, // $10 per 1M tokens
    },
  },
};

class APIUsageManager {
  private storageKey = 'reloop_api_usage';
  
  private getToday(): string {
    return new Date().toISOString().split('T')[0];
  }
  
  private getCurrentMinute(): string {
    const now = new Date();
    return `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;
  }
  
  // 使用量を記録
  recordUsage(
    provider: APIUsageData['provider'],
    inputTokens: number,
    outputTokens: number
  ): void {
    const today = this.getToday();
    const usageData = this.getUsageData();
    
    // 今日のデータを探すか新規作成
    let todayData = usageData.find(
      data => data.provider === provider && data.date === today
    );
    
    if (!todayData) {
      todayData = {
        provider,
        date: today,
        usage: {
          requests: 0,
          inputTokens: 0,
          outputTokens: 0,
          totalTokens: 0,
        },
      };
      usageData.push(todayData);
    }
    
    // 使用量を更新
    todayData.usage.requests += 1;
    todayData.usage.inputTokens += inputTokens;
    todayData.usage.outputTokens += outputTokens;
    todayData.usage.totalTokens += inputTokens + outputTokens;
    
    // コストを計算
    const quota = API_QUOTAS[provider];
    if (quota?.pricing) {
      const inputCost = (inputTokens / 1000) * quota.pricing.inputTokensPer1K;
      const outputCost = (outputTokens / 1000) * quota.pricing.outputTokensPer1K;
      todayData.usage.estimatedCost = (todayData.usage.estimatedCost || 0) + inputCost + outputCost;
    }
    
    this.saveUsageData(usageData);
    
    // 分単位の使用量も記録（レート制限チェック用）
    this.recordMinuteUsage(provider);
  }
  
  // 分単位の使用量を記録
  private recordMinuteUsage(provider: string): void {
    const key = `reloop_minute_usage_${provider}`;
    const minute = this.getCurrentMinute();
    const data = localStorage.getItem(key);
    
    let minuteData: Record<string, number> = {};
    if (data) {
      try {
        minuteData = JSON.parse(data);
      } catch (error) {
        console.error('Error parsing minute usage data:', error);
      }
    }
    
    minuteData[minute] = (minuteData[minute] || 0) + 1;
    
    // 古いデータを削除（2分以上前）
    const now = new Date();
    const twoMinutesAgo = new Date(now.getTime() - 2 * 60 * 1000);
    const cutoffTime = `${twoMinutesAgo.getHours()}:${String(twoMinutesAgo.getMinutes()).padStart(2, '0')}`;
    
    Object.keys(minuteData).forEach(time => {
      if (time < cutoffTime) {
        delete minuteData[time];
      }
    });
    
    localStorage.setItem(key, JSON.stringify(minuteData));
  }
  
  // レート制限をチェック
  checkRateLimit(provider: APIUsageData['provider']): {
    allowed: boolean;
    reason?: string;
    resetIn?: number; // seconds
  } {
    const quota = API_QUOTAS[provider];
    if (!quota) return { allowed: true };
    
    // 分単位のチェック
    if (quota.limits.requestsPerMinute) {
      const minuteUsage = this.getMinuteUsage(provider);
      if (minuteUsage >= quota.limits.requestsPerMinute) {
        return {
          allowed: false,
          reason: `分あたりのリクエスト上限（${quota.limits.requestsPerMinute}回）に達しました`,
          resetIn: 60,
        };
      }
    }
    
    // 日単位のチェック
    if (quota.limits.requestsPerDay) {
      const todayUsage = this.getTodayUsage(provider);
      if (todayUsage.requests >= quota.limits.requestsPerDay) {
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        const resetIn = Math.floor((tomorrow.getTime() - now.getTime()) / 1000);
        
        return {
          allowed: false,
          reason: `本日のリクエスト上限（${quota.limits.requestsPerDay}回）に達しました`,
          resetIn,
        };
      }
    }
    
    // トークン制限のチェック
    if (quota.limits.tokensPerMonth) {
      const monthUsage = this.getMonthUsage(provider);
      if (monthUsage.totalTokens >= quota.limits.tokensPerMonth) {
        const now = new Date();
        const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        const resetIn = Math.floor((nextMonth.getTime() - now.getTime()) / 1000);
        
        return {
          allowed: false,
          reason: `月間のトークン上限（${quota.limits.tokensPerMonth.toLocaleString()}トークン）に達しました`,
          resetIn,
        };
      }
    }
    
    return { allowed: true };
  }
  
  // 分単位の使用量を取得
  private getMinuteUsage(provider: string): number {
    const key = `reloop_minute_usage_${provider}`;
    const data = localStorage.getItem(key);
    
    if (!data) return 0;
    
    try {
      const minuteData = JSON.parse(data);
      return Object.values(minuteData).reduce((sum: number, count: any) => sum + (count as number), 0);
    } catch (error) {
      console.error('Error parsing minute usage data:', error);
      return 0;
    }
  }
  
  // 今日の使用量を取得
  getTodayUsage(provider: APIUsageData['provider']): APIUsageData['usage'] {
    const today = this.getToday();
    const usageData = this.getUsageData();
    
    const todayData = usageData.find(
      data => data.provider === provider && data.date === today
    );
    
    return todayData?.usage || {
      requests: 0,
      inputTokens: 0,
      outputTokens: 0,
      totalTokens: 0,
      estimatedCost: 0,
    };
  }
  
  // 今月の使用量を取得
  getMonthUsage(provider: APIUsageData['provider']): APIUsageData['usage'] {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const usageData = this.getUsageData();
    
    const monthData = usageData.filter(data => {
      if (data.provider !== provider) return false;
      const date = new Date(data.date);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });
    
    return monthData.reduce(
      (acc, data) => ({
        requests: acc.requests + data.usage.requests,
        inputTokens: acc.inputTokens + data.usage.inputTokens,
        outputTokens: acc.outputTokens + data.usage.outputTokens,
        totalTokens: acc.totalTokens + data.usage.totalTokens,
        estimatedCost: (acc.estimatedCost || 0) + (data.usage.estimatedCost || 0),
      }),
      {
        requests: 0,
        inputTokens: 0,
        outputTokens: 0,
        totalTokens: 0,
        estimatedCost: 0,
      }
    );
  }
  
  // 全期間の使用量を取得
  getAllTimeUsage(provider?: APIUsageData['provider']): Record<string, APIUsageData['usage']> {
    const usageData = this.getUsageData();
    
    const filteredData = provider
      ? usageData.filter(data => data.provider === provider)
      : usageData;
    
    const result: Record<string, APIUsageData['usage']> = {};
    
    filteredData.forEach(data => {
      const key = provider ? 'total' : data.provider;
      if (!result[key]) {
        result[key] = {
          requests: 0,
          inputTokens: 0,
          outputTokens: 0,
          totalTokens: 0,
          estimatedCost: 0,
        };
      }
      
      result[key].requests += data.usage.requests;
      result[key].inputTokens += data.usage.inputTokens;
      result[key].outputTokens += data.usage.outputTokens;
      result[key].totalTokens += data.usage.totalTokens;
      result[key].estimatedCost = (result[key].estimatedCost || 0) + (data.usage.estimatedCost || 0);
    });
    
    return result;
  }
  
  // 使用量データを取得
  private getUsageData(): APIUsageData[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return [];
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error loading usage data:', error);
      return [];
    }
  }
  
  // 使用量データを保存
  private saveUsageData(data: APIUsageData[]): void {
    // 古いデータを削除（90日以上前）
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    const cutoffDate = ninetyDaysAgo.toISOString().split('T')[0];
    
    const filteredData = data.filter(item => item.date >= cutoffDate);
    
    localStorage.setItem(this.storageKey, JSON.stringify(filteredData));
  }
  
  // 使用量データをリセット
  resetUsage(provider?: APIUsageData['provider']): void {
    if (provider) {
      const usageData = this.getUsageData();
      const filteredData = usageData.filter(data => data.provider !== provider);
      this.saveUsageData(filteredData);
      
      // 分単位のデータもリセット
      localStorage.removeItem(`reloop_minute_usage_${provider}`);
    } else {
      // 全データをリセット
      localStorage.removeItem(this.storageKey);
      ['gemini-free', 'gemini', 'claude', 'gpt'].forEach(p => {
        localStorage.removeItem(`reloop_minute_usage_${p}`);
      });
    }
  }
  
  // APIの制限情報を取得
  getQuotaInfo(provider: APIUsageData['provider']): APIQuota | undefined {
    return API_QUOTAS[provider];
  }
  
  // 残り使用可能量を計算
  getRemainingQuota(provider: APIUsageData['provider']): {
    daily?: { requests?: number; tokens?: number };
    monthly?: { tokens?: number };
    percentage?: number; // 0-100
  } {
    const quota = API_QUOTAS[provider];
    if (!quota) return {};
    
    const todayUsage = this.getTodayUsage(provider);
    const monthUsage = this.getMonthUsage(provider);
    
    const result: any = {};
    
    // 日次制限
    if (quota.limits.requestsPerDay || quota.limits.tokensPerDay) {
      result.daily = {};
      if (quota.limits.requestsPerDay) {
        result.daily.requests = Math.max(0, quota.limits.requestsPerDay - todayUsage.requests);
      }
      if (quota.limits.tokensPerDay) {
        result.daily.tokens = Math.max(0, quota.limits.tokensPerDay - todayUsage.totalTokens);
      }
    }
    
    // 月次制限
    if (quota.limits.tokensPerMonth) {
      result.monthly = {
        tokens: Math.max(0, quota.limits.tokensPerMonth - monthUsage.totalTokens),
      };
      result.percentage = ((quota.limits.tokensPerMonth - monthUsage.totalTokens) / quota.limits.tokensPerMonth) * 100;
    }
    
    return result;
  }
}

export const apiUsageManager = new APIUsageManager();